import {
  Container,
  Stack,
  Box,
  Text,
  Heading,
  Center,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaLinkedin, FaGithub, FaEnvelope } from "react-icons/fa";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import t from "../translations";

const MotionBox = motion(Box);

function SocialLink({ icon: Icon, href, label, color }) {
  const bg = useColorModeValue(`${color}.50`, "whiteAlpha.100");
  const hoverBg = useColorModeValue(`${color}.100`, "whiteAlpha.200");

  return (
    <motion.div
      whileHover={{ y: -3, scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      <Box
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        cursor="pointer"
        aria-label={label}
      >
        <Box
          p={{ base: 3, md: 4 }}
          borderRadius="full"
          bg={bg}
          _hover={{ bg: hoverBg }}
          transition="background 0.2s"
        >
          <Icon size={22} />
        </Box>
        <Text fontSize="sm" fontWeight={500}>
          {label}
        </Text>
      </Box>
    </motion.div>
  );
}

export default function Contact({ color }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });
  const textColor = useColorModeValue("gray.600", "gray.400");

  const socialLinks = [
    {
      icon: FaLinkedin,
      href: t.profile.linkedin,
      label: t.contact.linkedinLabel,
    },
    {
      icon: FaGithub,
      href: t.profile.github,
      label: t.contact.githubLabel,
    },
    ...(t.profile.email
      ? [
          {
            icon: FaEnvelope,
            href: `mailto:${t.profile.email}`,
            label: t.contact.emailLabel,
          },
        ]
      : []),
  ];

  return (
    <Container maxW="4xl" id="contact" py={{ base: 16, md: 24 }}>
      <MotionBox
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Stack spacing={8} textAlign="center">
          <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight={700}>
            {t.contact.heading}
          </Heading>
          <Text
            color={textColor}
            fontSize={{ base: "md", md: "lg" }}
            maxW="xl"
            mx="auto"
          >
            {t.contact.description}
          </Text>
          <Center>
            <HStack spacing={{ base: 6, md: 8 }}>
              {socialLinks.map(({ icon, href, label }) => (
                <SocialLink
                  key={label}
                  icon={icon}
                  href={href}
                  label={label}
                  color={color}
                />
              ))}
            </HStack>
          </Center>
        </Stack>
      </MotionBox>
    </Container>
  );
}
